/**
 * SOLID Review
 * S — Strong. One concern: the admin's LiveKit line — the media side's health, asked now and every 30 s for the admin it names.
 * O — Good. Another field of the answer is the view's.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The health, the view's one input.
 * D — Strong. One use case, the request contexts, the timers.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { IServiceViewTimers } from './platform/IConfigDepViewPlatform';
import type { ClientRequests } from '../client/ClientRequests';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

const LIVEKIT_HEALTH_POLL_MS = 30_000;

export interface IStateMediaHealth {
  readonly health: { readonly configured: boolean; readonly activeRooms: number; readonly activeParticipants: number } | null;
}

/** What the indicator's view hands it: the admin it is for. */
type THealthInputs = readonly [playerId: string];

const UNKNOWN: IStateMediaHealth = { health: null };

/**
 * THE ADMIN'S LIVEKIT LINE (frontend-framework-oo P4, 2026-10-29, §28.296) — `ControlPanel.tsx`'s `LiveKitHealthIndicator` state and
 * effect: the media side's `checkMediaHealth` (strict-ports FE5b) asked at once and every 30 s; a failed answer keeps the last one, and
 * an answer that comes back after the indicator went is dropped.
 */
export class MediaHealthPoller implements IServiceViewModel<IStateMediaHealth> {
  private readonly state = new ObservableState<IStateMediaHealth>(UNKNOWN);
  private readonly reactions = new Reactions<THealthInputs>([{
    deps: (inputs) => inputs,
    run: ([playerId]) => {
      let cancelled = false;
      const poll = async (): Promise<void> => {
        const request: TClientUseCaseInput<'checkMediaHealth'> = { playerId };
        const result = await this.useCases.checkMediaHealth(this.requests.context(), request);
        if (!cancelled && result.success) {
          const answered: Partial<IStateMediaHealth> = { health: result.value };
          this.state.update(answered);
        }
      };
      void poll();
      const interval = this.timers.setInterval(poll, LIVEKIT_HEALTH_POLL_MS);
      return () => {
        cancelled = true;
        this.timers.clearInterval(interval);
      };
    },
  }]);

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'checkMediaHealth'>,
    private readonly requests: ClientRequests,
    private readonly timers: IServiceViewTimers,
  ) {}

  getSnapshot(): IStateMediaHealth {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as THealthInputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as THealthInputs);
  }
}
